<template>
  <div class="pb-4 relative mt-2.5" @drop="handleDrop">
    <!-- 布局属性 对齐 x y w h rotation 等 -->
    <LayoutAttrs v-if="!hiddenLayoutAttrs" class="mt-2.5" :shape="shape" />

    <!-- 图片 -->
    <template v-if="shapeType == ShapeType.Image">
      <RecolorEnable v-if="showRecolorEnable" :shape="shape" />
      <Image v-else :shape="shape" />
    </template>

    <!-- 图组 -->
    <template v-if="shapeType == ShapeType.ImageArray">
      <RecolorEnable v-if="showRecolorEnable" :shape="shape" />
      <ImageValues v-else :shape="shape" />
    </template>

    <!-- 文本 -->
    <template v-if="shapeType == ShapeType.Text">
      <!-- <RecolorEnable v-if="showRecolorEnable" :shape="shape" /> -->
      <Text :shape="shape" />
    </template>

    <!-- 进度条 -->
    <template v-if="shapeType == ShapeType.ProgressBar">
      <RecolorEnable v-if="showRecolorEnable" :shape="shape" />
      <ProgressBar v-else :shape="shape" />
    </template>

    <!-- 序列帧 -->
    <template v-if="shapeType == ShapeType.Sprite">
      <RecolorEnable v-if="showRecolorEnable" :shape="shape" />
      <Sprite v-else :shape="shape" />
    </template>

    <!-- 容器 -->
    <template v-if="shapeType == ShapeType.Slot">
      <Slot :shape="shape" />
    </template>

    <!-- 应用小部件 -->
    <template v-if="shapeType == ShapeType.AppWidget">
      <AppWidget :shape="shape" />
    </template>

    <!--JS程序-->
    <template v-if="shapeType == ShapeType.JsFile">
      <RecolorEnable v-if="showRecolorEnable" :shape="shape" />
      <JsFile v-else :shape="shape" />
    </template>

    <!--Rive-->
    <template v-if="shapeType == ShapeType.Rive">
      <Rive :shape="shape" />
    </template>

    <!-- 相册或人像表盘背景 -->
    <template v-if="shapeType == ShapeType.PhotoAlbumBg">
      <RecolorEnable v-if="showRecolorEnable" :shape="shape" />
      <PhotoAlbumBg v-else :shape="shape" />
    </template>

    <!-- 文字表盘 -->
    <template v-if="shapeType == ShapeType.TextWatchface">
      <RecolorEnable v-if="showRecolorEnable" :shape="shape" />
      <TextWatchface v-else :shape="shape" />
    </template>

    <template v-if="shapeType == ShapeType.VideoWatchface">
      <RecolorEnable v-if="showRecolorEnable" :shape="shape" />
      <VideoWatchface v-else :shape="shape" />
    </template>

    <!-- 表达式 -->
    <template v-if="shapeType == ShapeType.Widget">
      <Group v-if="canUse('widget-flex').valid" :shape="shape" />
      <Widget :shape="shape" />
    </template>

    <!--APK程序-->
    <template v-if="shapeType == ShapeType.ApkFile">
      <RecolorEnable v-if="showRecolorEnable" :shape="shape" />
      <ApkFile v-else :shape="shape" />
    </template>

    <!-- 锁定显示的图层 -->
    <div
      v-if="
        (shape?.attrs._isLocked && shape.type !== ShapeType.PhotoAlbumBg) ||
        lockedIdsInCurShapeTree.includes(shape?.id) ||
        !shapeEnabeld
      "
      class="absolute z-20 top-0 left-0 bottom-0 w-full bg-[#202020]/60"
    >
      <div
        v-if="!shapeEnabeld"
        class="w-full h-full flex items-center justify-center"
      >
        <span
          class="text-base text-red-500 underline cursor-pointer"
          @click="deleteShape"
          >不支持该元素, 点击删除</span
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useEditorStore } from '@/store/use-editor-store';
import { Shape, ShapeType } from '@/services/shape';
import { EventType, emitter } from '@/utils/emitter.utils';
import { canUse } from '@/utils/can-use.utils';
import { lockedIdsInCurShapeTree } from '@/hooks';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { validShapeEnabled } from '@/utils/watchface-export/validShape';

const watchStore = useWatchfaceStore();
const editorStore = useEditorStore();
const shape = computed(() => editorStore.activeShape as Shape);
const shapeType = computed(() => shape.value?.type);
// 只有recolor的情况下等于all
const showRecolorEnable = computed(() => editorStore.attrDisabled == 'all');
const hiddenLayoutAttrs = computed(() => {
  return (
    showRecolorEnable.value &&
    [
      ShapeType.Image,
      ShapeType.ImageArray,
      ShapeType.Text,
      ShapeType.ProgressBar
    ].includes(shape.value.type)
  );
});

// useEmitterListener(
//   EventType['shape:set-attr'],
//   (data: { shapeId: string; attrs: (keyof Attributes)[] }) => {
//     const { shapeId } = data;
//     const shape = Shape.getShape(shapeId);
//     if (!shape) return;

//     // 需求变更，自动布局下子元素x y不可修改，但可修改尺寸大小，这种情况下不取消自动布局
//     // // 当子元素移动或改变大小时，取消父元素的自动布局
//     // const parent = shape.parent;
//     // if (
//     //   parent &&
//     //   parent.type == ShapeType.Group &&
//     //   ['x', 'y', 'w', 'h'].some((key) => attrs.includes(key as 'w'))
//     // ) {
//     //   // 取消gruop的自动布局
//     //   if (parent.attrs.flexDirection !== 'disabled') {
//     //     parent.setAttrs({ flexDirection: 'disabled' });
//     //   }
//     // }
//   }
// );

// 处理拖拽上传图片
const handleDrop = (e: DragEvent) => {
  if (!e.dataTransfer?.files) return;
  const files = Array.from(e.dataTransfer.files);
  if (
    files &&
    files.length &&
    files.filter((i) => i.type !== 'image/png').length === 0 &&
    files.filter((i) => !i.path).length === 0
  ) {
    emitter.emit(EventType['attrs:drop-images'], files);
    e.stopImmediatePropagation();
    e.stopPropagation();
  }
};

const shapeEnabeld = computed(() => {
  return validShapeEnabled({
    shape: shape.value,
    deviceType: watchStore.deviceType,
    themeType: editorStore.theme?.type ?? 'normal',
    watchOS: watchStore.description.watchOS ?? 'vela'
  });
});
const deleteShape = () => {
  shape.value.destroy();
};
</script>

<style scoped></style>
